// NoteDoc — the only module that knows the CRDT is Yjs (docs/adr/0003-crdt-yjs.md): swapping in
// Loro would mean rewriting this file only.
//
// A note = one document: text 'body' (Markdown) + map 'meta' (title, folder, color, pinned, due,
// archived, created). Metadata entries are last-writer-wins, which is what notd did already.

import * as Y from 'yjs';

export const REMOTE = Symbol('remote');

export type NoteFields = {
  title: string;
  folder: string | null;
  color: string | null;
  pinned: boolean;
  due: number | null;
  archived: boolean;
  created: number;
};

export const DEFAULT_FIELDS: NoteFields = { title: '', folder: null, color: null, pinned: false, due: null, archived: false, created: 0 };

export class NoteDoc {
  readonly ydoc: Y.Doc;
  readonly body: Y.Text;
  readonly meta: Y.Map<unknown>;

  constructor(state?: Uint8Array) {
    // gc stays on: the history is rebuilt from the journal, not from the CRDT (ADR 0003).
    this.ydoc = new Y.Doc();
    if (state) Y.applyUpdate(this.ydoc, state, REMOTE);
    this.body = this.ydoc.getText('body');
    this.meta = this.ydoc.getMap('meta');
  }

  static fromUpdates(updates: Uint8Array[]): NoteDoc {
    const doc = new NoteDoc();
    for (const u of updates) Y.applyUpdate(doc.ydoc, u, REMOTE);
    return doc;
  }

  text(): string {
    return this.body.toString();
  }

  fields(): NoteFields {
    const out = { ...DEFAULT_FIELDS };
    for (const key of Object.keys(DEFAULT_FIELDS) as (keyof NoteFields)[]) {
      if (this.meta.has(key)) (out as Record<string, unknown>)[key] = this.meta.get(key);
    }
    return out;
  }

  set(fields: Partial<NoteFields>, origin?: unknown): void {
    this.ydoc.transact(() => {
      for (const [k, v] of Object.entries(fields)) this.meta.set(k, v);
    }, origin);
  }

  /** Replaces [at, at+del) with `insert` — what the editor produces from one input event. */
  splice(at: number, del: number, insert: string, origin?: unknown): void {
    this.ydoc.transact(() => {
      if (del) this.body.delete(at, del);
      if (insert) this.body.insert(at, insert);
    }, origin);
  }

  apply(update: Uint8Array, origin: unknown = REMOTE): void {
    Y.applyUpdate(this.ydoc, update, origin);
  }

  encode(): Uint8Array {
    return Y.encodeStateAsUpdate(this.ydoc);
  }

  /** Called with every update and whether it came from outside (applied with REMOTE). */
  onUpdate(fn: (update: Uint8Array, remote: boolean) => void): () => void {
    const h = (u: Uint8Array, origin: unknown) => fn(u, origin === REMOTE);
    this.ydoc.on('update', h);
    return () => this.ydoc.off('update', h);
  }

  /** Cursor anchoring across remote edits. */
  anchor(index: number): Y.RelativePosition {
    return Y.createRelativePositionFromTypeIndex(this.body, index);
  }

  resolve(pos: Y.RelativePosition): number | null {
    return Y.createAbsolutePositionFromRelativePosition(pos, this.ydoc)?.index ?? null;
  }

  static merge(updates: Uint8Array[]): Uint8Array {
    return updates.length === 1 ? updates[0] : Y.mergeUpdates(updates);
  }
}
